import * as echarts from "echarts/core";
import { BarChart, LineChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
  type GridComponentOption,
  type LegendComponentOption,
  type TooltipComponentOption
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { ComposeOption, ECharts } from "echarts/core";

import { chartDisplayTrends, formatTrendDateLabel, last24HourTrends } from "./metrics";
import type { TrendPoint } from "./types";

echarts.use([BarChart, CanvasRenderer, GridComponent, LegendComponent, LineChart, TooltipComponent]);

type ChartOption = ComposeOption<GridComponentOption | LegendComponentOption | TooltipComponentOption>;

function dateLabels(trends: TrendPoint[]): string[] {
  return trends.map((point) => formatTrendDateLabel(point.timestamp));
}

function baseOption(labels: string[]): ChartOption {
  return {
    backgroundColor: "transparent",
    color: ["#3B82F6", "#93C5FD", "#22C55E", "#F59E0B"],
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(15, 23, 42, 0.96)",
      borderColor: "rgba(147, 197, 253, 0.28)",
      textStyle: { color: "#F8FAFC", fontFamily: "Inter" }
    },
    legend: {
      show: true,
      bottom: 0,
      itemWidth: 10,
      itemHeight: 8,
      textStyle: { color: "#94A3B8", fontFamily: "Inter", fontSize: 10, fontWeight: 700 }
    },
    grid: {
      left: 34,
      right: 10,
      top: 18,
      bottom: 42
    },
    xAxis: {
      type: "category",
      data: labels,
      axisLabel: {
        color: "rgba(148, 163, 184, 0.72)",
        fontFamily: "Inter",
        fontSize: 10,
        hideOverlap: true,
        lineHeight: 13
      },
      axisLine: { lineStyle: { color: "rgba(148, 163, 184, 0.18)" } },
      axisTick: { show: false }
    },
    yAxis: {
      type: "value",
      min: 0,
      splitLine: { lineStyle: { color: "rgba(148, 163, 184, 0.12)" } },
      axisLabel: { color: "rgba(148, 163, 184, 0.72)", fontFamily: "Inter", fontSize: 10 }
    }
  };
}

function lineChart(container: HTMLElement, labels: string[], series: Array<{ name: string; data: Array<number | null> }>): ECharts {
  const chart = echarts.init(container, undefined, { renderer: "canvas" });
  chart.setOption({
    ...baseOption(labels),
    series: series.map((item) => ({
      type: "line",
      name: item.name,
      data: item.data,
      showSymbol: false,
      smooth: false,
      connectNulls: true,
      lineStyle: { width: 2 },
      areaStyle: { opacity: item.name === "Congestion signal" ? 0.12 : 0 }
    }))
  });
  return chart;
}

function barChart(container: HTMLElement, labels: string[], series: Array<{ name: string; data: Array<number | null> }>): ECharts {
  const chart = echarts.init(container, undefined, { renderer: "canvas" });
  chart.setOption({
    ...baseOption(labels),
    series: series.map((item) => ({
      type: "bar",
      name: item.name,
      data: item.data,
      barMaxWidth: 14,
      itemStyle: { borderRadius: [3, 3, 0, 0] }
    }))
  });
  return chart;
}

export function renderCharts(trends: TrendPoint[]): () => void {
  const displayTrends = chartDisplayTrends(last24HourTrends(trends));
  const labels = dateLabels(displayTrends);
  const chartM10 = lineChart(document.getElementById("chart-m10") as HTMLElement, labels, [
    { name: "Congestion signal", data: displayTrends.map((point) => point.congestionScore) }
  ]);
  const chartDemand = barChart(document.getElementById("chart-demand") as HTMLElement, labels, [
    { name: "Anchorage", data: displayTrends.map((point) => point.anchoragePressure) },
    { name: "Surge 6h", data: displayTrends.map((point) => point.arrivalSurge6h) }
  ]);
  const chartFlow = lineChart(document.getElementById("chart-flow") as HTMLElement, labels, [
    { name: "Arrival", data: displayTrends.map((point) => point.arrivalRateHourly) },
    { name: "Departure", data: displayTrends.map((point) => point.departureRateHourly) }
  ]);

  const charts = [chartM10, chartDemand, chartFlow];
  const resize = (): void => {
    charts.forEach((chart) => chart.resize());
  };
  window.addEventListener("resize", resize);

  return () => {
    window.removeEventListener("resize", resize);
    charts.forEach((chart) => chart.dispose());
  };
}
